import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { GripVertical, Plus, Trash2, ShieldCheck, AlertTriangle, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, sortableKeyboardCoordinates, useSortable, arrayMove, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const TIER_LIMITS = {
  spark: { website: 9, app: 0 },
  "focus-web": { website: 9, app: 0 },
  focus: { website: 9, app: 3 },
  "sync-web": { website: 15, app: 0 },
  sync: { website: 15, app: 5 },
  momentum: { website: 20, app: 9 },
  command: { website: 30, app: 15 },
};

export default function HierarchyPage() {
  const { user, refresh } = useAuth();
  const isS = user?.role === "s_tier";
  const [websites, setWebsites] = useState([]);
  const [apps, setApps] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const limits = TIER_LIMITS[user?.tier] || { website: 9, app: 0 };

  const fetchAll = async () => {
    const { data } = await api.get("/hierarchy");
    setWebsites(data.websites || []);
    setApps(data.apps || []);
    try {
      const r = await api.get("/hierarchy/my-requests");
      setMyRequests(r.data.requests || []);
    } catch (_) {}
  };

  useEffect(() => {
    fetchAll();
    // clear dirty banner once they reach this page
    if (user?.hierarchy_dirty) {
      api.post("/hierarchy/clear-dirty").then(refresh).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveReorder = async (kind, items) => {
    items.forEach((it, i) => (it.rank = i + 1));
    const merged = [
      ...(kind === "website" ? items : websites),
      ...(kind === "app" ? items : apps),
    ];
    if (kind === "website") setWebsites([...items]);
    else setApps([...items]);
    try {
      await api.put("/hierarchy", { items: merged.map(({ name, kind, rank, category }) => ({ name, kind, rank, category })) });
      toast.success("Order saved.");
    } catch (e) {
      toast.error("Could not save order.");
    }
  };

  return (
    <div className="space-y-6" data-testid="hierarchy-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-[#2C6BAC] font-semibold">Priority hierarchy</p>
        <h1 className="font-display text-3xl text-[#1A1A2E] mt-1">Rank what matters.</h1>
        <p className="text-sm text-[#555568] mt-2">
          Websites and local apps are ranked independently. Items higher in each list are considered higher priority by WYLO.
        </p>
      </div>

      {user?.hierarchy_dirty && (
        <div className="rounded-xl border border-[#BA7517]/30 bg-[#BA7517]/10 p-4 flex items-start gap-3" data-testid="hierarchy-dirty-banner">
          <AlertTriangle className="text-[#BA7517] mt-0.5" size={18} />
          <div>
            <p className="text-sm font-semibold text-[#1A1A2E]">New items were added to your hierarchy.</p>
            <p className="text-xs text-[#555568]">S-Tier approved a new website or app for your workspace. Please re-rank your lists below.</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <KindCard
          title="Websites"
          subtitle={`${websites.length} of ${limits.website} slots`}
          items={websites}
          onReorder={(next) => saveReorder("website", next)}
          isS={isS}
          onAdminDelete={async (it) => {
            const next = websites.filter((x) => !(x.name === it.name && x.kind === it.kind));
            saveReorder("website", next);
          }}
          testid="hierarchy-websites"
        />
        <KindCard
          title="Local apps"
          subtitle={limits.app === 0 ? "Not available on your tier" : `${apps.length} of ${limits.app} slots`}
          items={apps}
          onReorder={(next) => saveReorder("app", next)}
          isS={isS}
          onAdminDelete={async (it) => {
            const next = apps.filter((x) => !(x.name === it.name && x.kind === it.kind));
            saveReorder("app", next);
          }}
          testid="hierarchy-apps"
        />
      </div>

      <RequestSection isS={isS} myRequests={myRequests} refetch={fetchAll} />

      <div className="rounded-xl bg-[#1A3A5C] text-white p-6">
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck size={16} />
          <p className="text-xs uppercase tracking-[0.18em] font-semibold">Add-on blocks</p>
        </div>
        <p className="text-sm text-[#E8ECF2]/85">Need more slots? Purchase a Social Media or Design app block of three from billing.</p>
      </div>
    </div>
  );
}

function KindCard({ title, subtitle, items, onReorder, isS, onAdminDelete, testid }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const itemKey = (it) => `${it.kind}::${it.name}`;
  const ids = items.map(itemKey);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = ids.indexOf(active.id);
    const newIndex = ids.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const next = arrayMove(items, oldIndex, newIndex);
    onReorder(next);
  };

  return (
    <section className="rounded-xl border border-[#E8ECF2] bg-white p-6" data-testid={testid}>
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="font-display text-xl font-bold text-[#1A1A2E]">{title}</h2>
        <span className="text-xs text-[#555568] font-mono">{subtitle}</span>
      </div>
      {items.length === 0 && (
        <p className="text-sm text-[#555568] italic">No items yet.</p>
      )}
      {items.length > 0 && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            <div className="space-y-2" data-testid={`${testid}-list`}>
              {items.map((it) => (
                <SortableRow
                  key={itemKey(it)}
                  id={itemKey(it)}
                  it={it}
                  isS={isS}
                  onAdminDelete={onAdminDelete}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
      {items.length > 1 && (
        <p className="text-[11px] text-[#555568] mt-3">Drag the handle to reorder. Higher items are higher priority.</p>
      )}
    </section>
  );
}

function SortableRow({ id, it, isS, onAdminDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 30 : "auto",
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 rounded-md border bg-[#F8F9FC] p-3 transition-colors ${
        isDragging ? "border-[#1A3A5C] shadow-lg bg-white" : "border-[#E8ECF2] hover:bg-white"
      }`}
      data-testid={`hierarchy-row-${id}`}
    >
      <button
        type="button"
        className="cursor-grab active:cursor-grabbing text-[#555568] hover:text-[#1A1A2E] touch-none"
        aria-label="Drag to reorder"
        data-testid={`hierarchy-drag-${id}`}
        {...attributes}
        {...listeners}
      >
        <GripVertical size={16} />
      </button>
      <span className="font-mono text-xs text-[#2C6BAC] w-8">#{it.rank}</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-[#1A1A2E] truncate">{it.name}</p>
        <p className="text-xs text-[#555568]">{it.category || it.kind}</p>
      </div>
      {isS && (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onAdminDelete(it)}
          className="text-[#E24B4A]"
          data-testid="hierarchy-admin-delete"
        >
          <Trash2 size={14} />
        </Button>
      )}
    </div>
  );
}

function RequestSection({ isS, myRequests, refetch }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", kind: "website", category: "", suggested_rank: 1 });
  const submit = async () => {
    if (!form.name.trim()) return;
    try {
      await api.post("/hierarchy/request", {
        action: "add",
        name: form.name.trim(),
        kind: form.kind,
        category: form.category.trim() || null,
        suggested_rank: parseInt(form.suggested_rank, 10) || 1,
      });
      toast.success(isS ? "Item added - confirm rank by reordering." : "Sent for S-Tier approval.");
      setOpen(false);
      setForm({ name: "", kind: "website", category: "", suggested_rank: 1 });
      refetch();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit request.");
    }
  };

  return (
    <section className="rounded-xl border border-[#E8ECF2] bg-white p-6">
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="font-display text-xl font-bold text-[#1A1A2E]">
          {isS ? "Add a new item" : "Request an addition"}
        </h2>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#2C6BAC] hover:bg-[#1A3A5C] text-white" data-testid="hierarchy-request-add">
              <Plus size={14} className="mr-1" /> {isS ? "Add" : "Request Add"}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{isS ? "Add a website or app" : "Request a website or app"}</DialogTitle>
              <DialogDescription>
                {isS
                  ? "Items added here will propagate to all users in this workspace."
                  : "Only S-Tier can add items directly. Your request will be reviewed and applied to your workspace."}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 mt-2">
              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-[#555568]">Kind</label>
                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="website">Website</SelectItem>
                    <SelectItem value="app">Local app</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-[#555568]">Name</label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={form.kind === "website" ? "e.g. github.com" : "e.g. Figma"} data-testid="hierarchy-request-name" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-[#555568]">Category</label>
                <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="e.g. Design, Communication" data-testid="hierarchy-request-category" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-[#555568]">Suggested rank (within {form.kind}s)</label>
                <Input type="number" min={1} value={form.suggested_rank} onChange={(e) => setForm({ ...form, suggested_rank: e.target.value })} />
              </div>
            </div>
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submit} className="bg-[#2C6BAC] hover:bg-[#1A3A5C] text-white" data-testid="hierarchy-request-submit">
                {isS ? "Add" : "Submit request"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {!isS && (
        <>
          <p className="text-xs text-[#555568] mb-3">
            All additions, edits, and deletions are reviewed by S-Tier and may be applied to every user in your workspace.
          </p>
          {myRequests.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs uppercase tracking-[0.18em] text-[#555568] font-semibold">My recent requests</p>
              {myRequests.slice(0, 5).map((r) => (
                <div key={r.id} className="flex items-center justify-between rounded-md border border-[#E8ECF2] bg-[#F8F9FC] p-3 text-sm">
                  <div>
                    <p className="font-semibold text-[#1A1A2E]">{r.name} <span className="text-xs text-[#555568] font-normal">· {r.kind} · {r.action}</span></p>
                    <p className="text-xs text-[#555568] font-mono">{new Date(r.created_at).toLocaleString()}</p>
                  </div>
                  <StatusBadge status={r.status} />
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: ["#BA7517", "Pending"],
    approved: ["#1D9E75", "Approved"],
    rejected: ["#E24B4A", "Rejected"],
  };
  const [color, label] = map[status] || ["#555568", status];
  return (
    <span className="text-[10px] uppercase tracking-[0.18em] font-semibold inline-flex items-center gap-1" style={{ color }}>
      {status === "approved" ? <Check size={10} /> : null}
      {label}
    </span>
  );
}
